import ExcelJS from 'exceljs';

/**
 * Liest das erste Tabellenblatt einer Prüfregel-Importdatei (.xlsx) als Zeilenobjekte, Schlüssel = Spaltenname.
 *
 * Die Vorlage enthält vor der eigentlichen Kopfzeile eine Titel- und eine Untertitelzeile
 * (s. `KiEditorComponent.downloadRuleImportTemplate`). Die Kopfzeile wird daher anhand der Spalte „Name"
 * gesucht, statt starr Zeile 1 anzunehmen – sonst entstehen falsche Spaltennamen und jede Zeile
 * erscheint fälschlich als "ohne Name".
 *
 * Ersetzt `XLSX.utils.sheet_to_json(sheet, {defval: ''})` aus SheetJS (`xlsx` 0.18.5 hat offene
 * Sicherheitslücken und wird auf npm nicht mehr aktualisiert):
 * - jede Spalte der Kopfzeile ist in jeder Zeile vorhanden, leere Zellen als `''`;
 * - doppelte Spaltennamen bekommen wie bei SheetJS `_1`, `_2`, … angehängt;
 * - Spalten ohne Namen und komplett leere Zeilen werden übersprungen.
 *
 * @returns `null`, wenn keine Kopfzeile mit der Spalte „Name" gefunden wurde.
 */
export async function readRuleImportRows(buffer: ArrayBuffer): Promise<Record<string, unknown>[] | null> {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const sheet = workbook.worksheets[0];
  if (!sheet) return null;

  let headerRowNumber = -1;
  sheet.eachRow((row, rowNumber) => {
    if (headerRowNumber !== -1) return;
    row.eachCell(cell => {
      if (String(toPlainValue(cell.value)).trim().toLowerCase() === 'name') headerRowNumber = rowNumber;
    });
  });
  if (headerRowNumber === -1) return null;

  const keyByColumn = new Map<number, string>();
  const seenKeys = new Map<string, number>();
  sheet.getRow(headerRowNumber).eachCell((cell, colNumber) => {
    const header = String(toPlainValue(cell.value));
    if (header.trim() === '') return;
    const count = seenKeys.get(header) ?? 0;
    seenKeys.set(header, count + 1);
    keyByColumn.set(colNumber, count === 0 ? header : `${header}_${count}`);
  });

  const rows: Record<string, unknown>[] = [];
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber <= headerRowNumber) return;
    const record: Record<string, unknown> = {};
    let hasValue = false;
    keyByColumn.forEach((key, colNumber) => {
      const value = toPlainValue(row.getCell(colNumber).value);
      record[key] = value;
      if (value !== '') hasValue = true;
    });
    if (hasValue) rows.push(record);
  });
  return rows;
}

/** Wandelt einen ExcelJS-Zellwert in den Rohwert um (Rich Text, Formel-Ergebnis, Hyperlink-Text). */
function toPlainValue(value: ExcelJS.CellValue): string | number | boolean {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'object') {
    if ('richText' in value) return value.richText.map(t => t.text).join('');
    if ('result' in value) return toPlainValue((value as {result: ExcelJS.CellValue}).result);
    if ('text' in value) return String((value as {text: unknown}).text);
    return '';
  }
  return value;
}
